import {Component, HostListener, OnInit} from '@angular/core';
import {formatCurrency, formatNumber} from '@angular/common';
import moment from 'moment';
import {ActivatedRoute, Router} from '@angular/router';
import {InvoicingService} from '../shared/invoicing.service';
import {Invoice, InvoiceIssue, InvoiceLine, InvoiceTripEntry} from '../interfaces/invoicing';
import {SubscribedComponent} from '../subscribed.component';
import {NotifierService} from '../shared/notifier.service';
import {TranslateService} from '@ngx-translate/core';
import {AuthService} from '../shared/auth.service';
import {UserStatus} from '../interfaces/user';
import {companyTabAllowed} from '../clients/company-tab/company-tabs';
import {IssueAction, IssueViewer, groupIssues, resolveIssue} from './invoice-issues';

// Narrows a resolved action for the template: line.default_rate can resolve to more than one
// permitted action (confirm the price, or set the project rate), every other code to at most one.
function isActionArray(a: IssueAction | IssueAction[] | 'admin'): a is IssueAction[] {
  return Array.isArray(a);
}

/**
 * One invoice (#1518). A draft: its lines with editable price and description, its trips to
 * leave off or put back, and what to know before finalising. A final invoice: read only, its
 * PDF, a retry when the PDF could not be made, and its credit in full (#1519).
 */
// What a change came to. Refused: the save failed and the reload shows what is stored. Failed: the reload failed too.
// Skipped: nothing was sent.
type Outcome = 'saved' | 'refused' | 'failed' | 'skipped';

// The 20 readiness keys (#1518 PR8b): each issue's code is the same string as its
// `invoice.issue.fix.<code>` string key and as the target field resolveIssue() matches on.
const READINESS_CODES = new Set([
  'company.name', 'company.street', 'company.postcode', 'company.town', 'company.country',
  'company.vat_id', 'company.kvk', 'company.iban', 'company.invoicing_starts_on',
  'client.name', 'client.street', 'client.postcode', 'client.town', 'client.country', 'client.kvk', 'client.vat_id', 'client.supplier_code',
  'project.vat_option', 'project.client_reference', 'project.client',
]);

@Component({
  selector: 'bst-invoice',
  templateUrl: './invoice.component.html',
  styleUrls: ['./invoice.component.scss'],
  standalone: false
})
export class InvoiceComponent extends SubscribedComponent implements OnInit {
  invoice: Invoice = null;
  failed = false;
  busy = false;
  intro = '';
  introOpen = false; // the intro field shows once asked for, or while the stored draft has an intro (so clearing it keeps the field until saved)
  excluding: number = null;
  reason = '';
  typed = new Set<number>();   // the lines whose price input was typed in since its last save
  described = new Set<number>(); // the lines whose description input was typed in since it was last left
  pending = new Set<string>(); // fields whose save failed and still show unsaved text: 'intro', 'price<line>', 'description<line>'
  private running: Promise<void> = Promise.resolve();
  private editing = new Set<Promise<void>>(); // field saves until they have marked their outcome
  private issueActions: (IssueAction | IssueAction[] | 'admin' | null)[] = []; // resolved once per load (show()), not per CD cycle

  constructor(private route: ActivatedRoute, private router: Router, private invoicing: InvoicingService, private notifier: NotifierService,
              private translate: TranslateService, private auth: AuthService) {
    super();
  }

  // The UI language, passed to the number and currency pipes: LOCALE_ID is fixed at bootstrap.
  get lang(): string {
    return this.translate.currentLang ?? this.translate.defaultLang;
  }

  ngOnInit() {
    this.subs.push(this.route.paramMap.subscribe(async params => {
      const id = +params.get('id');
      this.invoice = null;
      this.failed = false;
      this.introOpen = false;
      this.typed.clear();
      this.described.clear();
      this.pending.clear();
      const invoice = await this.invoicing.getInvoice(id).catch(() => null);
      if (this.current(id)) { this.failed = !invoice; this.show(invoice); }
    }));
  }

  // Leaving first leaves the focused field, so Back sends a typed edit as a click on a link does; then it waits for every
  // save to mark its outcome, and asks only if an edit is left unsaved.
  async canLeave(): Promise<boolean> {
    (document.activeElement as HTMLElement)?.blur();
    while (this.busy || this.editing.size) { await Promise.all([this.running, ...this.editing]); }
    if (!this.pending.size) { return true; }
    const check = await this.translate.get('invoice.leave_check').toPromise();
    return this.notifier.confirm('invoice.confirm_leave', check, 'invoice.leave', 'danger', 'cancel');
  }

  // A reload or closed tab cannot wait, so the browser asks for anything saving, failed, or typed and not yet sent.
  @HostListener('window:beforeunload', ['$event'])
  unload(event: BeforeUnloadEvent) {
    const introTyped = !!this.invoice && this.intro.trim() !== (this.invoice.intro ?? '');
    const reasonTyped = this.excluding !== null && !!this.reason.trim();
    if (this.busy || this.pending.size || this.typed.size || this.described.size || introTyped || reasonTyped) { event.preventDefault(); event.returnValue = true; }
  }

  get draft(): boolean {
    return this.invoice?.status === 'draft';
  }

  get creditable(): boolean {
    const inv = this.invoice;
    return inv.status === 'final' && inv.pdf_stored && !inv.credits && !inv.credited_by;
  }

  get tripCounts(): {included: number, off: number} {
    const included = this.invoice.trips.filter(t => t.included).length;
    return {included, off: this.invoice.trips.length - included + (this.invoice.not_chargeable?.length ?? 0)};
  }

  openIntro() {
    this.introOpen = true;
    setTimeout(() => document.getElementById('invoice-intro')?.focus());
  }

  // The intro grows with its text up to six lines, then scrolls; it never widens the card.
  grow(field: HTMLTextAreaElement) {
    field.style.height = 'auto';
    field.style.height = Math.min(field.scrollHeight + 2, 6 * 21 + 14) + 'px';
  }

  // An intro already on the draft opens at its own height: its line breaks, and long lines wrapping at about 90 characters.
  get introRows(): number {
    return Math.min(6, Math.max(2, this.intro.split('\n').reduce((n, l) => n + Math.max(1, Math.ceil(l.length / 90)), 0)));
  }

  get reverseCharge(): boolean {
    return this.invoice.vat_kind === 'reverse_charge';
  }

  // What the draft would charge now, excluding VAT.
  get net(): number {
    return this.invoice.lines.filter(l => l.entries).reduce((sum, l) => sum + (l.amount_cents ?? 0), 0);
  }

  // What the client would pay: the server's preview of the VAT, by the finaliser's own sum (null without a VAT option).
  get gross(): number {
    return this.net + (this.invoice.vat_preview_cents ?? 0);
  }

  // A final invoice charges one VAT rate, frozen on its lines.
  get finalRate(): number | null {
    return this.invoice.lines.find(l => l.vat_rate != null)?.vat_rate ?? null;
  }

  get unpriced(): boolean {
    return this.invoice.lines.some(l => l.entries && !l.has_price);
  }

  // Who is looking (#1518 PR8b): what the issue resolver needs to tell a permitted target from
  // 'Admin action required'. Mirrors company-tab.component.ts's own `invoicing` check.
  get viewer(): IssueViewer {
    const me = this.auth.user.getValue();
    return {id: me?.id, activeAdmin: this.auth.isAdmin && me?.user_to_client?.status === UserStatus.ACTIVE,
      staffer: this.auth.isStaffer, companyTabs: companyTabAllowed('billing', this.auth)};
  }

  get issues(): {blocking: InvoiceIssue[], review: InvoiceIssue[], info: InvoiceIssue[]} {
    return groupIssues(this.invoice.issues ?? []);
  }

  get blocking(): InvoiceIssue[] {
    return this.issues.blocking;
  }

  // Resolved once per load in show(), not recomputed on every change-detection pass.
  action(issue: InvoiceIssue): IssueAction | IssueAction[] | 'admin' | null {
    const i = this.invoice?.issues?.indexOf(issue) ?? -1;
    return i >= 0 ? this.issueActions[i] : null;
  }

  isArray(a: IssueAction | IssueAction[] | 'admin'): a is IssueAction[] {
    return isActionArray(a);
  }

  // A link never deletes directly (ruling: no delete without the confirm dialog); it focuses the
  // field or button the person asked for instead.
  focusLocal(a: IssueAction) {
    if (a.local?.line_id) { document.getElementById('line-' + a.local.line_id + '-price')?.focus(); }
    else if (a.local?.delete) { document.getElementById('invoice-delete-btn')?.focus(); }
  }

  get canLinkClient(): boolean {
    return this.auth.isAdmin || this.auth.isStaffer;
  }

  get period(): string {
    const day = (d: string) => moment(d).locale(this.lang).format('D MMM YYYY');
    return this.translate.instant('invoice.period_range', {from: day(this.invoice.period_from), to: day(this.invoice.period_to)});
  }

  // A readiness issue's code is its own fix key (e.g. 'company.iban'): its fix string is the
  // whole li, doubling as the action label resolveIssue returns for the same key. Every other
  // issue needs its own descriptive text alongside the action/admin slot.
  isReadiness(code: string): boolean {
    return READINESS_CODES.has(code);
  }

  // R1 (PR8b fix round 3): a readiness row whose only action's label is literally its own fix
  // key (e.g. company.iban -> 'fix.company.iban') would render that fix text twice: once as the
  // span, once as the action link. A multi-target row (isArray) or a differently-labelled action
  // (project.client -> action.delete_draft) still needs the span, so only the exact single-match
  // case is omitted.
  //
  // R1 regression (fix round 4): a one-element array (e.g. project.client_reference for an Admin
  // without companyTabs, which now resolves only its project target as a 1-length array per C2)
  // is still functionally a single action. Unwrap it before the label check, so it is treated the
  // same as the non-array case; a genuine multi-element array still keeps the span.
  duplicatesFix(issue: InvoiceIssue, a: IssueAction | IssueAction[] | 'admin'): boolean {
    const single = this.isArray(a) && (a as IssueAction[]).length === 1 ? (a as IssueAction[])[0] : a;
    return !this.isArray(single) && single !== 'admin' && (single as IssueAction).label === 'fix.' + issue.code;
  }

  issueText(issue: InvoiceIssue): string {
    const p = issue.params;
    const week = (d: string) => moment(d).isoWeek();
    const day = (d: string) => moment(d).locale(this.lang).format('D MMM YYYY');
    switch (issue.code) {
      case 'line.unpriced':
        return this.translate.instant('invoice.issue.text.unpriced', {description: p.description});
      case 'trip.not_chargeable':
        return this.translate.instant('invoice.issue.text.not_chargeable', {person: p.person, date: day(p.date), reason: this.translate.instant('invoice.not_chargeable.' + p.reason)});
      case 'line.default_rate':
        return this.translate.instant('invoice.warning.default_rate_confirm', {rate: formatCurrency(+this.invoice.default_member_rate, this.lang, '€', 'EUR'), people: p.person});
      case 'client.no_einvoice':
        return this.translate.instant('invoice.warning.no_einvoice', {client: this.invoice.client_name});
      case 'work.before_start': {
        const period = p.from === p.to ? day(p.from) : this.translate.instant('invoice.period_range', {from: day(p.from), to: day(p.to)});
        const work = [p.hundredths && `${formatNumber(p.hundredths / 100, this.lang, '1.2-2')} ${this.translate.instant('invoice.unit_hours')}`,
          p.trips && this.translate.instant(p.trips === 1 ? 'invoice.warning.trips_one' : 'invoice.warning.trips', {value: p.trips})].filter(Boolean).join(', ');
        return this.translate.instant('invoice.issue.text.before_start', {work, person: p.person, period});
      }
      case 'work.unapproved': {
        const work = [p.hundredths && `${formatNumber(p.hundredths / 100, this.lang, '1.2-2')} ${this.translate.instant('invoice.unit_hours')}`,
          p.trips && this.translate.instant(p.trips === 1 ? 'invoice.warning.trips_one' : 'invoice.warning.trips', {value: p.trips})].filter(Boolean).join(', ');
        const state = this.translate.instant('invoice.issue.text.state.' + (p.status ? p.status.toLowerCase() : 'none'));
        return this.translate.instant('invoice.issue.text.unapproved', {work, person: p.person, week: week(p.week_start), state});
      }
      case 'work.unallocated': {
        const work = [p.hundredths && `${formatNumber(p.hundredths / 100, this.lang, '1.2-2')} ${this.translate.instant('invoice.unit_hours')}`,
          p.trips && this.translate.instant(p.trips === 1 ? 'invoice.warning.trips_one' : 'invoice.warning.trips', {value: p.trips})].filter(Boolean).join(', ');
        return this.translate.instant('invoice.issue.text.unallocated', {work});
      }
      case 'hours.orphan':
        return this.translate.instant('invoice.issue.text.orphan', {hours: formatNumber(p.hundredths / 100, this.lang, '1.2-2'), person: p.person, week: week(p.week_start)});
      default:
        return '';
    }
  }

  get warnings(): {key: string, value: string | number}[] {
    const w = this.invoice.warnings;
    if (!w) { return []; }
    return [
      {key: 'unapproved', value: w.unapproved_hundredths && formatNumber(w.unapproved_hundredths / 100, this.lang, '1.2-2')},
      {key: 'unallocated', value: w.unallocated_hundredths && formatNumber(w.unallocated_hundredths / 100, this.lang, '1.2-2')},
      {key: w.unapproved_trips === 1 ? 'unapproved_trips_one' : 'unapproved_trips', value: w.unapproved_trips},
      {key: w.unallocated_trips === 1 ? 'unallocated_trips_one' : 'unallocated_trips', value: w.unallocated_trips},
    ].filter(x => x.value);
  }

  // Hours billed at the member rate a new user gets, which may be a placeholder; finalise still goes.
  get defaultRate(): {rate: string, people: string} | null {
    const lines = this.invoice.warnings?.default_rate ?? [];
    return lines.length ? {rate: formatCurrency(+this.invoice.default_member_rate, this.lang, '€', 'EUR'),
      people: [...new Set(lines.map(l => l.person))].join(', ')} : null;
  }

  // Per person, the approved work a draft asked from before the start date left out.
  get beforeStart(): {person: string, period: string, work: string}[] {
    const day = (d: string) => moment(d).locale(this.lang).format('D MMM YYYY');
    return (this.invoice.warnings?.before_start ?? []).map(b => ({person: b.person,
      period: b.from === b.to ? day(b.from) : this.translate.instant('invoice.period_range', {from: day(b.from), to: day(b.to)}),
      work: [b.hundredths && `${formatNumber(b.hundredths / 100, this.lang, '1.2-2')} ${this.translate.instant('invoice.unit_hour')}`,
        b.trips && this.translate.instant(b.trips === 1 ? 'invoice.warning.trips_one' : 'invoice.warning.trips', {value: b.trips})].filter(Boolean).join(', ')}));
  }

  // A download says what was downloaded: the PDF, the e-invoice (format ubl), or the PDF in the ZIP of all invoices.
  // A ZIP of a date range or one month names it; one from before either filter was a ZIP of all invoices.
  eventKey(e: Invoice['events'][number]): string {
    const d = e.data ?? {};
    return 'invoice.event.' + e.event + (d.export ? '_' + d.export : d.format ? '_' + d.format : '') + (d.from ? '_range' : d.month ? '_month' : '');
  }

  eventParams(e: Invoice['events'][number]): object {
    const d = e.data ?? {};
    return {old: d.old ?? '–', new: d.new ?? '–', number: d.number, line: d.line, reason: d.reason ? `: ${d.reason}` : '',
      trip: d.date && `${d.person}, ${moment(d.date).locale(this.lang).format('ddd D MMM')}`,
      from: d.from && moment(d.from).locale(this.lang).format('D MMM YYYY'), to: d.to && moment(d.to).locale(this.lang).format('D MMM YYYY'),
      month: d.month && new Date(`${d.month}-15`).toLocaleDateString(this.translate.currentLang, {month: 'long', year: 'numeric'})};
  }

  unit(line: InvoiceLine): string {
    return line.kind === 'km' ? 'invoice.unit_km' : 'invoice.unit_hour';
  }

  // Lines grouped under an hours/kilometres heading (#1518 PR8b).
  get hourLines(): InvoiceLine[] {
    return this.invoice.lines.filter(l => l.kind !== 'km');
  }

  get kmLines(): InvoiceLine[] {
    return this.invoice.lines.filter(l => l.kind === 'km');
  }

  async saveLine(line: InvoiceLine, change: {unit_price?: string, description?: string}, id = this.invoice.id) {
    return this.run(() => this.invoicing.saveLine(id, line.id, change));
  }

  // Afterwards the input shows what is stored, so a refused description does not linger looking saved, unless it was retyped.
  // Sent on blur when it differs from what is stored, so tabbing past sends nothing and leaving a failed one again retries it.
  saveDescription(line: InvoiceLine, input: HTMLInputElement): Promise<void> {
    return this.edit(async () => {
      const [id, description] = [this.invoice.id, input.value];
      this.described.delete(line.id);
      const stored = () => this.invoice.lines.find(l => l.id === line.id)?.description ?? '';
      const outcome = await this.queue(id, () => this.invoicing.saveLine(id, line.id, {description}), () => stored() !== description.trim());
      if (this.invoice?.id !== id) return;
      this.mark('description' + line.id, outcome);
      if (outcome !== 'failed' && input.value === description) { input.value = stored(); }
    });
  }

  // The stored price as typed in the UI language: a decimal comma in Dutch. The server takes either.
  priceInput(price: string | null | undefined): string {
    return price == null ? '' : this.lang === 'nl' ? price.replace('.', ',') : price;
  }

  // On blur, not change: retyping the same price is a change event short, and it confirms the rate. Tabbing past sends nothing.
  // A price left while another change is saving waits for it rather than being dropped. Afterwards the input shows what is
  // stored, so a refused price does not linger looking saved, unless it was typed in again meanwhile. A failed one counts as
  // typed again, so leaving it retries.
  savePrice(line: InvoiceLine, input: HTMLInputElement): Promise<void> {
    return this.edit(async () => {
      if (!this.typed.has(line.id)) return;
      const id = this.invoice.id;
      while (this.busy) { await this.running; }
      if (!this.typed.delete(line.id)) return;
      const outcome = await this.saveLine(line, {unit_price: input.value}, id);
      if (this.invoice?.id !== id) return;
      this.mark('price' + line.id, outcome);
      if (outcome === 'failed') { this.typed.add(line.id); }
      else if (!this.typed.has(line.id)) { input.value = this.priceInput(this.invoice.lines.find(l => l.id === line.id)?.unit_price); }
    });
  }

  // Checked for a change when it is sent, not when left, so leaving twice during a save sends it once. Trimmed, as the
  // server stores it, so trailing spaces are no change.
  saveIntro(): Promise<void> {
    return this.edit(async () => {
      const [id, typed] = [this.invoice.id, this.intro];
      const intro = typed.trim();
      const outcome = await this.queue(id, () => this.invoicing.saveIntro(id, intro), () => (this.invoice.intro ?? '') !== intro);
      if (this.invoice?.id !== id) return;
      this.mark('intro', outcome);
      if (outcome !== 'failed' && this.intro === typed) { this.intro = this.invoice.intro ?? ''; }
    });
  }

  exclude(trip: InvoiceTripEntry) {
    [this.excluding, this.reason] = [trip.id, ''];
  }

  // Its buttons are disabled while busy; Enter in the reason input is too, so the form stays open rather than closing unsent.
  async saveTrip(trip: InvoiceTripEntry, included: boolean) {
    if (this.busy) { return; }
    this.excluding = null;
    await this.run(() => this.invoicing.saveTrip(this.invoice.id, trip.id, included, included ? null : this.reason.trim() || null));
  }

  // Also the retry of a final invoice whose PDF could not be made; only a draft asks first. Refused while a field shows
  // text that failed to save, so the invoice never goes out with what is stored instead of what is on screen.
  async finalise() {
    if (this.unsaved()) { return; }
    const rate = this.draft && this.defaultRate;
    const beforeStart = this.draft && (this.beforeStart.length || this.issues.review.some(i => i.code === 'work.before_start'));
    const notes = [rate && await this.translate.get('invoice.warning.default_rate_confirm', rate).toPromise(),
      this.draft && this.invoice.warnings?.no_einvoice && await this.translate.get('invoice.warning.no_einvoice_confirm', {client: this.invoice.client_name}).toPromise(),
      beforeStart && await this.translate.get('invoice.issue.text.before_start_confirm').toPromise()];
    const note = notes.filter(Boolean).join(' ');
    const total = {client: this.invoice.client_name, total: formatCurrency(this.gross / 100, this.lang, '€', 'EUR')};
    if (this.draft && !await this.sure('finalise', 'primary', note, total)) { return; }
    if (this.unsaved()) { return; }
    await this.run(() => this.invoicing.finalise(this.invoice.id));
  }

  async remove() {
    if (this.busy || !await this.sure('delete', 'danger') || this.busy) { return; }
    const done = await this.hold(this.invoicing.removeInvoice(this.invoice.id).catch(() => null));
    if (done) { this.router.navigate(['/projects', this.invoice.project.id]); }
  }

  async credit() {
    const params = {number: this.invoice.number, total: formatCurrency(this.invoice.gross_cents / 100, this.lang, '€', 'EUR')};
    if (this.busy || !await this.sure('credit', 'danger', '', params) || this.busy) { return; }
    const credit = await this.hold(this.invoicing.credit(this.invoice.id).catch(() => null));
    if (credit) { this.router.navigate(['/invoices', credit.id]); }
  }

  async download() {
    await this.invoicing.downloadInvoice(this.invoice.id).catch(() => null);
  }

  async downloadUbl() {
    await this.invoicing.downloadUbl(this.invoice.id).catch(() => null);
  }

  // The dialog shows its subtitle as given, so translate it first.
  private async sure(action: 'delete' | 'finalise' | 'credit', status: 'primary' | 'danger', note = '', params?: object): Promise<boolean> {
    const check = await this.translate.get(`invoice.${action}_check`, params).toPromise();
    return this.notifier.confirm(`invoice.confirm_${action}`, note ? `${check} ${note}` : check, `invoice.${action}`, status, 'cancel');
  }

  // Every change answers with the whole invoice; a refused one reloads it, so the screen never shows an unsaved value.
  // An answer that lands after the route moved to another invoice is not shown. Unless both the save and the reload failed,
  // what is stored is known and the edited fields show it; after a failure they keep the typed text, the server state unknown.
  private async run(change: () => Promise<Invoice>): Promise<Outcome> {
    if (this.busy) { return 'skipped'; }
    const id = this.invoice.id;
    let outcome: Outcome = 'saved';
    const invoice = await this.hold(change().catch(async () => {
      const stored = await this.invoicing.getInvoice(id).catch(() => null);
      outcome = stored ? 'refused' : 'failed';
      return stored;
    }));
    if (invoice && this.current(invoice.id)) { this.show(invoice); }
    return outcome;
  }

  // A change made while another is saving waits for it rather than being dropped; skipped if the screen moved to another invoice.
  // The wait and the send must stay in one function: run() has to start in the same turn the wait ends, before another waiter.
  // wanted is asked after the wait, against what is stored by then.
  private async queue(id: number, change: () => Promise<Invoice>, wanted = () => true): Promise<Outcome> {
    while (this.busy) { await this.running; }
    return this.invoice?.id === id && wanted() ? this.run(change) : 'skipped';
  }

  // Tracked from its first line, so a save started by leaving the field is waited for by canLeave in the same turn.
  private edit(flow: () => Promise<void>): Promise<void> {
    const done = flow();
    this.editing.add(done);
    done.catch(() => {}).then(() => this.editing.delete(done));
    return done;
  }

  private mark(field: string, outcome: Outcome) {
    if (outcome === 'failed') { this.pending.add(field); } else { this.pending.delete(field); }
  }

  private unsaved(): boolean {
    if (this.pending.size) { this.notifier.notify('invoice.unsaved', 'error', 'danger'); }
    return this.pending.size > 0;
  }

  private current(id: number): boolean {
    return id === +this.route.snapshot.paramMap.get('id');
  }

  // Busy until the request settles; running settles only after busy is cleared, so a price waiting on it never spins.
  private hold<T>(request: Promise<T>): Promise<T> {
    this.busy = true;
    const done = request.finally(() => this.busy = false);
    this.running = done.then(() => {}, () => {});
    return done;
  }

  // An intro being typed survives another change landing; it is only replaced when it was not edited.
  private show(invoice: Invoice) {
    const edited = this.invoice && this.intro !== (this.invoice.intro ?? '');
    this.invoice = invoice;
    if (!edited) { this.intro = invoice?.intro ?? ''; }
    this.issueActions = (invoice?.issues ?? []).map(i => resolveIssue(i, this.viewer, {project_id: invoice.project.id}));
  }
}
